import { CommonModule } from '@angular/common';
import {
  Component, ChangeDetectionStrategy, Input, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef, Output, EventEmitter
} from '@angular/core';
import { isEmpty } from '@app/util/util';

import {
  DraggableTouchCarouselTouchEvent, DraggableTouchCarouselHeight, DraggableTouchCarouselLimit,
  DraggableTouchCarouselTouchMove, DraggableTouchCarouselMouseEvent
} from '@design-system/components/organisms/draggable-touch-carousel/entities/draggable-touch-carousel.types';
import { PaginationType } from '@design-system/components/molecules/pagination/entities/pagination.types';
import { ImageSize } from '@design-system/components/molecules/image/entities/image.types';
import { BorderRadiusDsType } from '@design-system/entities/border-radius.type';
import { PaginationDsComponent } from '@design-system/components/molecules/pagination/pagination.component';
import { ImageDsComponent } from '@design-system/components/molecules/image/image.component';

@Component({
  selector: 'app-ds-draggable-touch-carousel',
  templateUrl: './draggable-touch-carousel.component.html',
  styleUrls: ['./draggable-touch-carousel.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    PaginationDsComponent,
    ImageDsComponent,
  ]
})
export class DraggableTouchCarouselDsComponent implements AfterViewInit {

  @Input() paginationType = PaginationType.Base;
  @Input() images: string[] = [];
  @Input() autoplay = false;
  @Input() draggable = false;
  @Input() height: DraggableTouchCarouselHeight = null;
  @Input() borderRadius: BorderRadiusDsType = null;

  @Output() currentSlideValue: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild('slider') slider: ElementRef;

  public currentSlide = 1;
  public slidePosition = 0;

  private dragStartHasBeenCalled = false;
  private dragInitPosX = null;
  private dragInitPosXPercentage = 0;
  private dragSlidePositionBak = 0;

  public readonly imageSize = ImageSize.None;

  constructor(
    private readonly cdr: ChangeDetectorRef
  ) { }

  ngAfterViewInit(): void {
    const sliderRef = this.slider?.nativeElement;
    if (this.draggable && sliderRef) {
      this.addEventsForElement(sliderRef);
    }
  }

  get isVisible(): boolean {
    return !isEmpty(this.images) && !!this.height;
  }

  public trackById(index: number): number {
    return index;
  }

  public moveSlide(event: { page: number }): void {
    this.slidePosition = 100 * (event.page - 1);
    this.currentSlide = event.page;
    this.currentSlideValue.emit(this.currentSlide);
    this.resetDragValues();
  }

  private addEventsForElement(el: HTMLElement): void {
    el.addEventListener(DraggableTouchCarouselMouseEvent.mousedown, this.dragStart.bind(this));
    el.addEventListener(DraggableTouchCarouselMouseEvent.mouseup, this.dragEnd.bind(this));
    el.addEventListener(DraggableTouchCarouselMouseEvent.mousemove, this.dragAction.bind(this));
    el.addEventListener(DraggableTouchCarouselMouseEvent.mouseleave, this.dragCancel.bind(this));

    el.addEventListener(DraggableTouchCarouselTouchEvent.touchstart, this.dragStart.bind(this));
    el.addEventListener(DraggableTouchCarouselTouchEvent.touchend, this.dragEnd.bind(this));
    el.addEventListener(DraggableTouchCarouselTouchEvent.touchmove, this.dragAction.bind(this));
    el.addEventListener(DraggableTouchCarouselTouchEvent.touchcancel, this.dragCancel.bind(this));
  }

  private dragStart(ev: MouseEvent | TouchEvent): void {
    this.dragStartHasBeenCalled = true;
    this.dragInitPosX = this.getPosXPercentage(ev);
    this.dragSlidePositionBak = this.slidePosition;
    this.cdr.detectChanges();
  }

  private dragAction(ev: MouseEvent | TouchEvent): void {
    if (this.dragStartHasBeenCalled) {
      const dynamicPosX = this.getPosXPercentage(ev);
      if (dynamicPosX < this.dragInitPosX && this.currentSlide < this.images?.length) {
        this.dragInitPosXPercentage = this.dragInitPosXPercentage + DraggableTouchCarouselTouchMove;
        this.slidePosition = this.slidePosition + DraggableTouchCarouselTouchMove;
      } else if (dynamicPosX > this.dragInitPosX && this.currentSlide > 1) {
        this.dragInitPosXPercentage = this.dragInitPosXPercentage - DraggableTouchCarouselTouchMove;
        this.slidePosition = this.slidePosition - DraggableTouchCarouselTouchMove;
      }
      this.cdr.detectChanges();
    }
  }

  private dragEnd(): void {
    this.closingDrag();
    this.cdr.detectChanges();
  }

  private dragCancel(): void {
    this.closingDrag();
    this.cdr.detectChanges();
  }

  private getPosXPercentage(ev: MouseEvent | TouchEvent): number {
    const rect = this.slider?.nativeElement?.getBoundingClientRect();
    const eventPos = this.getTouchOrCursorPosition(ev);
    const width = rect.width;
    const posX = eventPos?.[0] - rect.left;
    return (posX * 100) / width;
  }

  private closingDrag(): void {
    if ((this.dragInitPosXPercentage > DraggableTouchCarouselLimit) && (this.currentSlide < this.images?.length)) {
      this.moveSlide({ page: this.currentSlide + 1 })
    } else if ((this.dragInitPosXPercentage < (DraggableTouchCarouselLimit * -1)) && (this.currentSlide > 1)) {
      this.moveSlide({ page: this.currentSlide - 1 })
    } else if (this.dragStartHasBeenCalled) {
      this.slidePosition = this.dragSlidePositionBak;
      this.resetDragValues();
    }
  }

  private resetDragValues(): void {
    this.dragStartHasBeenCalled = false;
    this.dragInitPosX = null;
    this.dragInitPosXPercentage = 0;
    this.dragSlidePositionBak = 0;
  }

  private getTouchOrCursorPosition(ev: MouseEvent | TouchEvent): number[] {
    if (Object.values(DraggableTouchCarouselTouchEvent).includes(ev.type as DraggableTouchCarouselTouchEvent)) {
      const touchEvent = ev as TouchEvent;
      const touch = touchEvent.touches?.[0] || touchEvent.changedTouches?.[0];
      return [touch?.pageX ?? 0, touch?.pageY ?? 0];
    } else if (Object.values(DraggableTouchCarouselMouseEvent).includes(ev.type as DraggableTouchCarouselMouseEvent)) {
      const mouseEvent = ev as MouseEvent;
      return [mouseEvent?.clientX ?? 0, mouseEvent.clientY ?? 0];
    }
  }
}
